/**
 * Autocomplete de unidade (104 unidades da rede) para vincular a uma conta
 * `unidade`. Digita-se para filtrar por nome/município; ao escolher, guarda o
 * `id` da unidade. Sem dependências — input + lista filtrada. Reusado no modal
 * de criar/editar usuário (desktop e mobile).
 */
import { UNIDADES } from "@caint/data";
import { useEffect, useMemo, useRef, useState } from "react";

const MAX_SUGESTOES = 8;

export function UnidadeAutocomplete({
  id,
  valor,
  onChange,
  className = "m-input",
  ariaErro,
}: {
  id: string;
  /** unidadeId selecionado ("" = nenhum). */
  valor: string;
  onChange: (unidadeId: string) => void;
  className?: string;
  /** Id do elemento com a mensagem de erro do campo — marca o input como inválido. */
  ariaErro?: string;
}) {
  const selecionada = UNIDADES.find((u) => u.id === valor) ?? null;
  const [busca, setBusca] = useState(selecionada?.nome ?? "");
  const [aberto, setAberto] = useState(false);

  const sugestoes = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const base = q
      ? UNIDADES.filter((u) => `${u.nome} ${u.municipio ?? ""}`.toLowerCase().includes(q))
      : UNIDADES;
    return base.slice(0, MAX_SUGESTOES);
  }, [busca]);

  const escolher = (u: (typeof UNIDADES)[number]) => {
    onChange(u.id);
    setBusca(u.nome);
    setAberto(false);
  };

  // O campo costuma ser o último do formulário: a lista abria para fora da área
  // visível do modal/folha e só 2 sugestões apareciam. Rola o mínimo necessário
  // para ela caber inteira.
  const lista = useRef<HTMLUListElement>(null);
  const nSugestoes = sugestoes.length;
  useEffect(() => {
    if (aberto && nSugestoes > 0) {
      lista.current?.scrollIntoView?.({ block: "nearest" });
    }
  }, [aberto, nSugestoes]);

  return (
    <div style={{ position: "relative" }}>
      <input
        id={id}
        className={className}
        value={busca}
        placeholder="Digite para buscar a unidade…"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        aria-invalid={ariaErro ? true : undefined}
        aria-describedby={ariaErro}
        onChange={(e) => {
          setBusca(e.target.value);
          setAberto(true);
          // Ao editar o texto, invalida a seleção — força escolher da lista de
          // novo (garante que o id guardado sempre bate com o que está escrito).
          if (valor) {
            onChange("");
          }
        }}
        onFocus={() => setAberto(true)}
        // Delay para o clique/toque na sugestão registrar antes de fechar.
        onBlur={() => setTimeout(() => setAberto(false), 150)}
      />
      {aberto && sugestoes.length > 0 && (
        <ul
          ref={lista}
          style={{
            position: "absolute",
            zIndex: 30,
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            margin: 0,
            padding: 4,
            listStyle: "none",
            maxHeight: 240,
            overflowY: "auto",
            background: "var(--surface, #fff)",
            border: "1px solid var(--surface-3, #e2e0d8)",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          }}
        >
          {sugestoes.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  background: u.id === valor ? "var(--surface-2, #eef1f7)" : "transparent",
                  borderRadius: 8,
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontSize: 14,
                }}
                // onMouseDown dispara ANTES do onBlur do input (senão a lista fecha
                // e o clique se perde).
                onMouseDown={() => escolher(u)}
              >
                {u.nome}
                {u.municipio && <span className="spec"> · {u.municipio}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
